import React, { useEffect, useState, useCallback } from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useAuth } from './AuthContext';
import { sendEmailVerification, signOut } from 'firebase/auth';
import { auth } from './firebase';
import { useNavigate } from 'react-router-dom';

export default function EmailVerificationPage() {
  const { currentUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (currentUser?.emailVerified) {
      navigate('/dashboard');
    }
  }, [currentUser?.emailVerified, navigate]);

  const handleResendVerificationEmail = async () => {
    if (currentUser) {
      try {
        await sendEmailVerification(currentUser);
        alert('Verification email sent!');
      } catch (error) {
        console.error('Error resending verification email:', error);
        alert('Failed to resend verification email.');
      }
    }
  };

  const handleRefresh = useCallback(async () => {
    if (checking || !currentUser) return;
    setChecking(true);
    try {
      await refreshUser();
    } catch (error) {
      if (error.code === 'auth/user-token-expired') {
        alert("Your session has expired. Please log in again.");
        navigate('/login');
      } else {
        // Don't alert on other errors during polling
        console.error("Error refreshing user status:", error);
      }
    }
    setChecking(false);
  }, [refreshUser, checking, navigate, currentUser]);

  useEffect(() => {
    handleRefresh(); // Initial check

    const intervalId = setInterval(handleRefresh, 5000);
    window.addEventListener('focus', handleRefresh);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', handleRefresh);
    };
  }, [handleRefresh]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Failed to sign out. Please try again.');
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography component="h1" variant="h5">
          Verify Your Email
        </Typography>
        <Box sx={{ mt: 3 }}>
          <Typography variant="body1">
            A verification email has been sent to your email address. Please click the link in the email to verify your account.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            This page will automatically update once you have verified your email.
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleResendVerificationEmail}
              disabled={!currentUser}
            >
              Resend Verification Email
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={handleSignOut}
            >
              Go to Login
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
